import { useEffect, useState } from 'react';
import { fetchStoreBuckets, fetchStoreForecasts, fetchStoreSummary } from '../api/storesApi';
import { fetchKPIHistory } from '../api/dashboardApi';

/**
 * 店舗タブのデータ読み込み。キーが変わったら取り直し、古い応答は捨てる。
 * enabled が false（月がまだ決まっていない・権限が無い）のときは、何も取らず、空のまま。
 * 失敗は error に入れる（画面側で「読み込めませんでした」を出す）。
 */
function useLoaded(key, enabled, load) {
  const [state, setState] = useState({ data: null, loading: Boolean(enabled), error: null });

  useEffect(() => {
    if (!enabled) {
      setState({ data: null, loading: false, error: null });
      return undefined;
    }
    let cancelled = false;
    setState((s) => ({ data: s.data, loading: true, error: null }));
    load()
      .then((data) => !cancelled && setState({ data, loading: false, error: null }))
      .catch((error) => !cancelled && setState({ data: null, loading: false, error }));
    return () => {
      cancelled = true;
    };
    // load は key から作られる（key が同じなら同じ取得）。毎回新しくなる関数を、依存に入れない
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, enabled]);

  return state;
}

/** 見える店舗すべての数字（誰でも）。「この店舗」「全店比較」の両方が、この 1 回の取得を使う。 */
export function useStoreSummary(month) {
  return useLoaded(`summary|${month}`, Boolean(month), () => fetchStoreSummary(month));
}

/** 店舗ごとの人数（管理者ロールだけ）。enabled が false なら、サーバーに問い合わせない。 */
export function useStoreBuckets(month, enabled) {
  return useLoaded(`buckets|${month}`, Boolean(month) && Boolean(enabled), () => fetchStoreBuckets(month));
}

/** 店舗合計の月次推移（stylist 指定なし）。 */
export function useStoreHistory(store) {
  return useLoaded(`history|${store}`, Boolean(store), () => fetchKPIHistory({ store }));
}

/** 店舗合計の予測（stylist 指定なし）。失敗しても、グラフは予測の線が出ないだけ。 */
export function useStoreForecasts(store) {
  return useLoaded(`forecasts|${store}`, Boolean(store), () => fetchStoreForecasts(store));
}
